import api from './api';

export type ReconciliationState = 'NONE' | 'RESERVED' | 'FINALIZED' | 'REVERSED' | 'INCONSISTENT';
export type IssueSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

export interface PosGovernanceRow {
  // Identity
  venueId: string;
  sourceOrderRef: string;
  sourceLineRef: string;
  // State
  reconciliationState: ReconciliationState;
  reconciliationCompleteness: string;
  // Counts
  counts: Record<string, number>;
  // Issues
  issueCodes: string[];
  highestSeverity: IssueSeverity | null;
  needsAdminAction: boolean;
  // Classification flags — mirrors the full backend governance row contract
  hasDuplicateActiveReservations?: boolean;
  hasDuplicateSaleMovements?: boolean;
  hasUnexpectedReservations?: boolean;
  hasReservationWithoutInstruction?: boolean;
  hasMovementWithoutInstruction?: boolean;
  hasPartialReservationCoverage?: boolean;
  hasPartialSaleCoverage?: boolean;
  hasPartialReversalCoverage?: boolean;
  hasMixedComponentStates?: boolean;
  // Latest activity timestamp
  latestActivityAt: string | null;
}

export interface PosGovernanceResponse {
  rows: PosGovernanceRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface PosGovernanceParams {
  limit?: number;
  offset?: number;
  needsAdminAction?: boolean;
  reconciliationState?: ReconciliationState;
  sourceOrderRef?: string;
  /** Repeatable — each value sent as a separate issueCode= param. */
  issueCodes?: string[];
}

// ── Reconciliation summary types (Phase 6 Step 1 read model) ─────────────────

export type PosReconciliationScope = 'TUPLE' | 'LINE';
/** Lowercase severity as used by the reconciliation summary (distinct from IssueSeverity). */
export type PosReconciliationSummaryIssueSeverity = 'info' | 'warning' | 'error';

export interface PosReconciliationExpectedComponent {
  componentKey: string;
  inventoryItemId: string;
  locationId: string;
  quantity: number;
  componentLineIndex: number | null;
}

export interface PosReconciliationExpectedSet {
  mappingMode: 'DIRECT' | 'RECIPE' | 'NONE';
  instructionPresent: boolean;
  components: PosReconciliationExpectedComponent[];
  expectedKeys: string[];
}

export interface PosReconciliationReservationEntry {
  reservationId: string;
  inventoryItemId: string;
  locationId: string;
  quantity: number;
  status: 'ACTIVE' | 'RELEASED';
  componentLineIndex: number | null;
  componentKey: string;
  sourceOrderRef: string;
  sourceLineRef: string;
  createdAt: string;
  updatedAt: string;
  releasedAt: string | null;
}

export interface PosReconciliationMovementEntry {
  movementId: string;
  inventoryItemId: string;
  locationId: string | null;
  quantity: number;
  movementType: 'SALE' | 'SALE_REVERSAL';
  componentLineIndex: number | null;
  componentKey: string;
  sourceLineRef: string | null;
  postedAt: string;
}

export interface PosReconciliationIssue {
  code: string;
  severity: PosReconciliationSummaryIssueSeverity;
  message: string;
  componentKey?: string;
  componentLineIndex?: number;
  evidence?: Record<string, unknown>;
}

export interface PosReconciliationSummary {
  venueId: string;
  sourceOrderRef: string;
  sourceLineRef: string;
  scope: PosReconciliationScope;
  reconciliationCompleteness: 'FULL' | 'PARTIAL';
  posMenuItemRef: string | null;
  locationId: string | null;
  expected: PosReconciliationExpectedSet;
  activeReservations: PosReconciliationReservationEntry[];
  releasedReservations: PosReconciliationReservationEntry[];
  saleMovements: PosReconciliationMovementEntry[];
  reversalMovements: PosReconciliationMovementEntry[];
  reconciliationState: ReconciliationState;
  issues: PosReconciliationIssue[];
}

const base = (venueId: string) => `/admin/venue/${venueId}/pos-inventory/governance`;

const posGovernanceService = {
  getGovernanceRows: async (
    venueId: string,
    params: PosGovernanceParams = {},
  ): Promise<PosGovernanceResponse> => {
    const query: Record<string, unknown> = {};
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.offset !== undefined) query.offset = params.offset;
    if (params.needsAdminAction !== undefined) query.needsAdminAction = params.needsAdminAction;
    if (params.reconciliationState) query.reconciliationState = params.reconciliationState;
    if (params.sourceOrderRef) query.sourceOrderRef = params.sourceOrderRef;
    if (params.issueCodes?.length) query.issueCode = params.issueCodes;
    // indexes: null → repeatable params: issueCode=A&issueCode=B (not issueCode[]=A)
    const response = await api.get(base(venueId), {
      params: query,
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  /**
   * Release duplicate ACTIVE reservations for one source line tuple.
   * Keeps one survivor per component key (earliest by createdAt, then _id).
   * Phase 6 Step 2 repair — reservation-only, no movement writes.
   */
  /**
   * Post compensating SALE_REVERSAL movements for extra SALE movements in
   * duplicate groups. Phase 6 Step 4 repair — ledger-immutable write.
   * Optional precision hints (posMenuItemRef, locationId, lineQuantity) are
   * omitted here; the backend resolves them from its own read model.
   */
  reverseDuplicateSaleMovements: async (
    venueId: string,
    sourceOrderRef: string,
    sourceLineRef: string,
  ): Promise<void> => {
    await api.post(
      `/admin/venue/${venueId}/pos-inventory/line/repair/reverse-duplicate-sale-movements`,
      { sourceOrderRef, sourceLineRef },
    );
  },

  releaseStaleReservations: async (
    venueId: string,
    sourceOrderRef: string,
    sourceLineRef: string,
  ): Promise<void> => {
    await api.post(
      `/admin/venue/${venueId}/pos-inventory/line/repair/release-stale-reservations`,
      { sourceOrderRef, sourceLineRef },
    );
  },

  releaseDuplicateReservations: async (
    venueId: string,
    sourceOrderRef: string,
    sourceLineRef: string,
  ): Promise<void> => {
    await api.post(
      `/admin/venue/${venueId}/pos-inventory/line/repair/release-duplicate-reservations`,
      { sourceOrderRef, sourceLineRef },
    );
  },

  /** Tuple-level reconciliation summary — no mapping context required. */
  getReconciliationSummary: async (
    venueId: string,
    sourceOrderRef: string,
    sourceLineRef: string,
  ): Promise<PosReconciliationSummary> => {
    const response = await api.get(
      `/admin/venue/${venueId}/pos-inventory/line/reconciliation-summary`,
      { params: { sourceOrderRef, sourceLineRef } },
    );
    return response.data;
  },
};

export default posGovernanceService;
